import React from "react";
import { motion, useReducedMotion } from "framer-motion";

/**
 * Motion that explains rather than decorates: things arrive in reading order, cards lift under the
 * pointer, numbers and bars fill to their value. Everything honours "reduce motion" — the content is
 * identical, it just appears.
 */
const EASE = [0.22, 1, 0.36, 1] as const;

export function FadeIn({ children, delay = 0, y = 12, style }: { children: React.ReactNode; delay?: number; y?: number; style?: React.CSSProperties }) {
  const reduce = useReducedMotion();
  if (reduce) return <div style={style}>{children}</div>;
  return (
    <motion.div style={style} initial={{ opacity: 0, y }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45, ease: EASE, delay }}>
      {children}
    </motion.div>
  );
}

/** A list whose children arrive one after another. Wrap each child in <StaggerItem>. */
export function Stagger({ children, gap = 0.05, style }: { children: React.ReactNode; gap?: number; style?: React.CSSProperties }) {
  const reduce = useReducedMotion();
  if (reduce) return <div style={style}>{children}</div>;
  return (
    <motion.div style={style} initial="hidden" animate="show" variants={{ hidden: {}, show: { transition: { staggerChildren: gap } } }}>
      {children}
    </motion.div>
  );
}

export function StaggerItem({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const reduce = useReducedMotion();
  if (reduce) return <div style={style}>{children}</div>;
  return (
    <motion.div style={style} variants={{ hidden: { opacity: 0, y: 14, scale: 0.985 }, show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: EASE } } }}>
      {children}
    </motion.div>
  );
}

/** Lifts a card a touch under the pointer and presses it on tap. */
export function Lift({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  const reduce = useReducedMotion();
  if (reduce) return <div style={{ height: "100%", ...style }}>{children}</div>;
  return (
    <motion.div style={{ height: "100%", ...style }} whileHover={{ y: -3 }} whileTap={{ scale: 0.985 }} transition={{ type: "spring", stiffness: 400, damping: 30 }}>
      {children}
    </motion.div>
  );
}

/** Renders `value` counting up from 0 (or from its previous value). Final value at once in tests and for reduced motion. */
export function useCountUp(value: number, duration = 0.9): number {
  const reduce = useReducedMotion();
  const skip = reduce || process.env.NODE_ENV === "test";
  const [shown, setShown] = React.useState(skip ? value : 0);
  const from = React.useRef(skip ? value : 0);
  React.useEffect(() => {
    if (skip) { setShown(value); return; }
    let raf = 0;
    const start = performance.now();
    const a = from.current;
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 3);
      setShown(Math.round(a + (value - a) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
      else from.current = value;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, duration, skip]);
  return shown;
}
